/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { isPromiseBasedObservable } from 'mobx-utils';
import { LazyObservablePromise, PromiseLikeUnwrap } from '../../lib';
import { isProduction } from '../../config';

type ExtractViews<Views> = {
    [Property in keyof Views]: PromiseLikeUnwrap<Views[Property]>;
};

type IntermediateView<Views> = {
    [Property in keyof Views]?: PromiseLikeUnwrap<Views[Property]>;
};

type ElementTypeOrFunction<T> = React.FunctionComponent<T>;

// Both React.memo and mobx observer uses this symbol for memoization of
// components. It will be used to detect the absense of observer when
// using Await elements.

const ReactMemoSymbol =
    typeof Symbol === 'function' && Symbol.for
        ? Symbol.for('react.memo')
        : typeof React.memo === 'function' &&
          // eslint-disable-next-line react/display-name
          React.memo(() => null).$$typeof;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isMemo(component: any): boolean {
    return (
        component && ReactMemoSymbol && component.$$typeof === ReactMemoSymbol
    );
}

// eslint-disable-next-line
function warnAboutMissingMemo<Props>(
    component: ElementTypeOrFunction<Props> | undefined,
    suppressWarning: boolean
): void {
    if (isProduction() || suppressWarning || !component || isMemo(component))
        return;

    console.warn(
        `Await: Component named '${component.displayName || 'unnamed'}' ` +
            `does not have an observer. It will not react to changes ` +
            `on the views.`
    );
}

// eslint-disable-next-line
function renderOrCall<Props extends {}>(
    component: ElementTypeOrFunction<Props> | null,
    props: Props
) {
    if (component === null) return null;

    return React.createElement<Props>(component, props, null);
}

const errorSet = new Set<Error>();

const throwError = observer(({ error }: { error: Error }) => {
    if (!errorSet.has(error)) {
        errorSet.add(error);
        throw error;
    }
    return null;
});

function Data({
    data,
    resolved,
    rejected,
}: {
    data: any;
    resolved: any;
    rejected: any;
}) {
    const [status, result] = data();

    if (status === 'rejected') return renderOrCall(rejected, result);
    return renderOrCall(resolved, result);
}

function fetchData<
    Views extends Record<string, LazyObservablePromise<unknown>>,
    Extra extends Record<string, unknown> = Record<string, never>
>({ views, extra }: { views: Views; extra?: Extra }) {
    const promises = Object.values(views).filter(isPromiseBasedObservable);
    let status = 'pending';
    let result: any = null;

    const handleFullfiled = () => {
        status = 'fulfilled';
        const readProps: IntermediateView<Views> = {};
        for (const i in views) {
            const promise = views[i];
            readProps[i] = promise.value as PromiseLikeUnwrap<typeof promise>;
        }
        const finalProps = {
            ...((extra || {}) as Extra),
            ...(readProps as ExtractViews<Views>),
        };
        result = finalProps;
    };

    const handleRejected = (error: any) => {
        status = 'rejected';
        result = { error };
    };

    const fetching = Promise.all(Object.values(promises))
        .then(handleFullfiled)
        .catch(handleRejected);
    // eslint-disable-next-line complexity
    return () => {
        // Check if the promise is fullfiled before halting to suspense.
        // This is done because if the promise is create already fullfiled,
        // the first execution still throws a pending, since the 'then' clause
        // hasn't executed yet.
        if (
            status === 'pending' &&
            Object.values(promises).every(p => p.state === 'fulfilled')
        ) {
            handleFullfiled();
        }
        if (status === 'pending') throw fetching;
        if (status === 'rejected' || status === 'fulfilled')
            return [status, result];
    };
}

// This need refactoring of course
function AwaitBase<
    Views extends Record<string, LazyObservablePromise<unknown>>
>({
    resolved,
    rejected,
    pending,
    views,
    suppressWarning,
}: {
    views: Views;
    resolved: ElementTypeOrFunction<ExtractViews<Views>>;
    pending?: ElementTypeOrFunction<Record<string, never>>;
    rejected?: ElementTypeOrFunction<{ error: Error }>;
    suppressWarning?: boolean;
}): JSX.Element | null;
function AwaitBase<
    Views extends Record<string, LazyObservablePromise<unknown>>,
    Extra extends Record<string, unknown>
>({
    resolved,
    rejected,
    pending,
    views,
    suppressWarning,
    extra,
}: {
    views: Views;
    resolved: ElementTypeOrFunction<ExtractViews<Views> & Extra>;
    pending?: ElementTypeOrFunction<Extra>;
    rejected?: ElementTypeOrFunction<{ error: Error }>;
    suppressWarning?: boolean;
    extra: Extra;
}): JSX.Element | null;
function AwaitBase<
    Views extends Record<string, LazyObservablePromise<unknown>>,
    Extra extends Record<string, unknown> = Record<string, never>
>({
    resolved,
    rejected = throwError,
    pending,
    views,
    suppressWarning = false,
    extra,
}: {
    views: Views;
    resolved:
        | ElementTypeOrFunction<ExtractViews<Views> & Extra>
        | ElementTypeOrFunction<ExtractViews<Views>>;
    pending?: ElementTypeOrFunction<Record<string, never> | Extra>;
    rejected?: ElementTypeOrFunction<{ error: Error }>;
    suppressWarning?: boolean;
    extra?: Extra;
}): JSX.Element | null {
    warnAboutMissingMemo(
        resolved as ElementTypeOrFunction<ExtractViews<Views> & Extra>,
        suppressWarning
    );

    return (
        <React.Suspense fallback={renderOrCall(pending || null, extra ?? {})}>
            <Data
                resolved={resolved}
                rejected={rejected}
                data={fetchData({ views, extra })}
            />
        </React.Suspense>
    );
}

const Await = observer(AwaitBase);

export { Await };
